import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Stack, ThemeProvider, Typography } from '@mui/material';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { DialogTitle } from './dialog-componentes';
import { criarTemaApp } from '../theme';

/**
 * Leitor de QR pela câmera do aparelho.
 *
 * A permissão de câmera é do sistema e NÃO tem como ser dispensada por código —
 * navegador nenhum deixa. Para o aparelho parar de perguntar, a permissão tem que
 * ser concedida uma vez e memorizada: no Android pelo site (ou pela concessão
 * automática do Fully Kiosk) e no iPad pelo ajuste do site / Ajustes do iOS.
 *
 * A câmera liga quando o diálogo abre e é desligada em TODA saída (fechar, ler,
 * erro, desmontar, app em segundo plano). Tablet de mesa fica ligado o dia
 * inteiro, já com wake lock: câmera aberta à toa esquenta o aparelho — e câmera
 * acesa apontada para o cliente é problema de percepção, não só de bateria.
 *
 * Decodificação: usa o `BarcodeDetector` do sistema quando ele existe de fato
 * (Android/Chrome), e só aí baixa o `jsQR` como fallback — nenhum navegador do
 * iPad tem o detector nativo, e no iPad TODO navegador é WebKit, então instalar
 * outro não muda nada. O import é dinâmico para o decodificador não entrar no
 * pacote do cliente de delivery, que nunca vai escanear nada.
 */

const INTERVALO_LEITURA_MS = 200;

// A luz vem do branco: iluminando, o leitor é claro mesmo com o tablet no tema escuro.
const TEMA_ILUMINADO = criarTemaApp('light');

type Camera = 'environment' | 'user';

interface DetectorNativo {
  detect: (fonte: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
}

interface JanelaComDetector {
  BarcodeDetector?: {
    new(opcoes: { formats: string[] }): DetectorNativo;
    getSupportedFormats?: () => Promise<string[]>;
  };
}

/** Detector do sistema, só se ele realmente decodifica QR (existir a classe não basta). */
async function detectorNativo(): Promise<DetectorNativo | null> {
  const janela = window as unknown as JanelaComDetector;
  if (!janela.BarcodeDetector) return null;

  try {
    const formatos = (await janela.BarcodeDetector.getSupportedFormats?.()) ?? [];
    if (!formatos.includes('qr_code')) return null;
    return new janela.BarcodeDetector({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

/**
 * Mensagem por motivo real da falha — "erro ao abrir a câmera" não ajuda ninguém.
 *
 * A saída muda conforme a loja: onde digitar existe, manda digitar; onde a
 * leitura é o caminho único, manda chamar o garçom — sem alternativa na tela,
 * dizer "informe o número" seria mandar o cliente fazer algo impossível.
 */
function mensagemDoErro(e: unknown, podeDigitar: boolean): string {
  const nome = (e as { name?: string } | null)?.name ?? '';
  const saida = podeDigitar ? 'informe o número da comanda.' : 'chame o garçom.';

  if (nome === 'NotAllowedError' || nome === 'SecurityError')
    return `A câmera foi bloqueada neste aparelho. Libere nos ajustes do sistema ou ${saida}`;
  if (nome === 'NotFoundError' || nome === 'OverconstrainedError')
    return `Não encontramos câmera neste aparelho. Por favor, ${saida}`;
  if (nome === 'NotReadableError')
    return 'A câmera está em uso por outro aplicativo. Feche-o e tente de novo.';
  // getUserMedia nem existe fora de contexto seguro (http em rede local).
  return `Não foi possível abrir a câmera aqui. Por favor, ${saida}`;
}

export function LeitorQr({
  aberto,
  titulo = 'Aponte para o QR Code',
  descricao,
  aoLer,
  aoFechar,
  aoPedirDigitar,
  podeDigitar = true,
  iluminar = false,
}: {
  aberto: boolean;
  titulo?: string;
  descricao?: string;
  aoLer: (valor: string) => void;
  aoFechar: () => void;
  /** Mostra o atalho "prefiro digitar" — a saída quando a câmera não coopera. */
  aoPedirDigitar?: () => void;
  /** A loja permite digitar? Só muda o texto da falha; o atalho vem de `aoPedirDigitar`. */
  podeDigitar?: boolean;
  /**
   * Tablet: tela inteira branca em volta da câmera frontal. No salão escuro a própria tela
   * ilumina a etiqueta da comanda.
   */
  iluminar?: boolean;
}): JSX.Element {
  const refVideo = useRef<HTMLVideoElement>(null);
  const refCanvas = useRef<HTMLCanvasElement>(null);
  const refStream = useRef<MediaStream | null>(null);
  const refTimer = useRef<number | null>(null);
  const refLido = useRef(false);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  // Frontal por padrão: o leitor só existe no tablet, que fica preso no suporte
  // virado para o cliente — a traseira aponta para a parede.
  const [camera, setCamera] = useState<Camera>('user');

  const parar = useCallback(() => {
    if (refTimer.current !== null) {
      window.clearInterval(refTimer.current);
      refTimer.current = null;
    }
    refStream.current?.getTracks().forEach((t) => t.stop());
    refStream.current = null;
    const video = refVideo.current;
    if (video) video.srcObject = null;
  }, []);

  useEffect(() => {
    if (!aberto) {
      parar();
      return;
    }

    refLido.current = false;
    setErro(null);
    setCarregando(true);
    let cancelado = false;

    async function iniciar(): Promise<void> {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new TypeError('sem getUserMedia');

        const stream = await navigator.mediaDevices.getUserMedia({
          // `ideal` e não exigência: tablet com uma câmera só ainda abre, em vez
          // de estourar OverconstrainedError e mandar o cliente digitar.
          video: { facingMode: { ideal: camera } },
          audio: false,
        });

        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        refStream.current = stream;
        const video = refVideo.current;
        if (video) {
          video.srcObject = stream;
          // playsInline: sem ele o iOS abre o vídeo em tela cheia própria.
          video.setAttribute('playsinline', 'true');
          await video.play().catch(() => undefined);
        }
        setCarregando(false);

        const nativo = await detectorNativo();
        // Só baixa o decodificador em JS quando o sistema não tem o dele.
        const jsQR = nativo ? null : (await import('jsqr')).default;

        refTimer.current = window.setInterval(() => {
          void (async () => {
            if (refLido.current) return;
            const v = refVideo.current;
            const c = refCanvas.current;
            if (!v || !c || v.readyState < 2 || !v.videoWidth) return;

            c.width = v.videoWidth;
            c.height = v.videoHeight;
            const ctx = c.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;
            ctx.drawImage(v, 0, 0, c.width, c.height);

            let valor: string | null = null;
            if (nativo) {
              const achados = await nativo.detect(c).catch(() => []);
              valor = achados[0]?.rawValue ?? null;
            } else if (jsQR) {
              const imagem = ctx.getImageData(0, 0, c.width, c.height);
              valor = jsQR(imagem.data, imagem.width, imagem.height, { inversionAttempts: 'dontInvert' })?.data ?? null;
            }

            if (!valor) return;
            refLido.current = true;
            parar();
            aoLer(valor);
          })();
        }, INTERVALO_LEITURA_MS);
      } catch (e) {
        if (cancelado) return;
        setCarregando(false);
        setErro(mensagemDoErro(e, podeDigitar));
      }
    }

    void iniciar();

    const aoMudarVisibilidade = (): void => {
      if (document.visibilityState === 'hidden') parar();
    };
    document.addEventListener('visibilitychange', aoMudarVisibilidade);

    return () => {
      cancelado = true;
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
      parar();
    };
  }, [aberto, camera, aoLer, parar, podeDigitar]);

  const dialogo = (
    <Dialog
      open={aberto}
      onClose={aoFechar}
      fullWidth
      maxWidth="sm"
      fullScreen={iluminar}
      PaperProps={iluminar ? { sx: { bgcolor: '#fff' } } : undefined}
    >
      <DialogTitle onClose={aoFechar} component="div">
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        {descricao && (
          <Typography variant="body2" color="text.secondary">
            {descricao}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={iluminar ? { p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' } : { p: 2 }}>
        {erro ? (
          <Alert severity="warning">{erro}</Alert>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              ...(iluminar ? { maxWidth: 640 } : {}),
              aspectRatio: '4/3',
              bgcolor: 'common.black',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              component="video"
              ref={refVideo}
              muted
              playsInline
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />

            <Box
              sx={{
                position: 'absolute',
                inset: '18%',
                border: '3px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                boxShadow: '0 0 0 9999px rgba(0,0,0,.35)',
              }}
            />

            {carregando && (
              <Stack sx={{ position: 'absolute', inset: 0 }} alignItems="center" justifyContent="center">
                <CircularProgress sx={{ color: 'common.white' }} />
              </Stack>
            )}
          </Box>
        )}

        <canvas ref={refCanvas} style={{ display: 'none' }} />
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, pl: 0 }}>
        {!erro && (
          <Button
            startIcon={<CameraswitchIcon />}
            onClick={() => setCamera((c) => (c === 'environment' ? 'user' : 'environment'))}
          >
            Trocar câmera
          </Button>
        )}

        {aoPedirDigitar && (
          <Button variant="contained" startIcon={<KeyboardIcon />} onClick={aoPedirDigitar} sx={{ textTransform: 'none' }}>
            Digitar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  return iluminar ? <ThemeProvider theme={TEMA_ILUMINADO}>{dialogo}</ThemeProvider> : dialogo;
}
